/**
 * Customer-facing PUBLIC cancel page.
 * URL: /payment/cancel?session_id={CHECKOUT_SESSION_ID}
 * When the customer aborts Stripe checkout, they land here. We fire a fire-and-forget
 * POST to /api/payments/{session_id}/mark-cancelled so the admin sees the correct status.
 */
import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { XCircle, RefreshCcw, Home } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PaymentCancel() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get('session_id');
  const [paymentLink, setPaymentLink] = useState(null);

  const isAr = true;

  useEffect(() => {
    if (!sessionId) return;
    // Mark cancelled (idempotent, safe on 404)
    axios
      .post(`${BACKEND_URL}/api/payments/${sessionId}/mark-cancelled`)
      .catch(() => {});
    // Fetch to grab a fresh retry link (only useful if status is still pending/failed)
    axios
      .get(`${BACKEND_URL}/api/payments/status/${sessionId}`)
      .then((r) => {
        if (r.data?.payment_link) setPaymentLink(r.data.payment_link);
      })
      .catch(() => {});
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#030303] flex items-center justify-center px-4 py-10">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full blur-[140px] bg-slate-500/10" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.4 }}
        className="relative z-10 w-full max-w-lg bg-white/[0.03] backdrop-blur-2xl border border-slate-500/40 rounded-3xl p-8 sm:p-10 text-center"
        data-testid="payment-cancel-card"
      >
        <motion.div
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.15, type: 'spring', stiffness: 220 }}
          className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center bg-slate-500/15 border-2 border-slate-500"
        >
          <XCircle className="w-10 h-10 text-slate-400" />
        </motion.div>

        <h1
          className="text-3xl sm:text-4xl font-black text-white mb-3"
          data-testid="payment-cancel-title"
        >
          {isAr ? 'تم إلغاء الدفع' : 'Payment Cancelled'}
        </h1>
        <p className="text-white/70 mb-8 text-sm sm:text-base leading-relaxed">
          {isAr
            ? 'قمت بإلغاء عملية الدفع. يمكنك المحاولة مرة أخرى إذا رغبت.'
            : 'You cancelled the payment. You can try again if you like.'}
        </p>

        {paymentLink && (
          <a
            href={paymentLink}
            className="inline-flex items-center gap-2 w-full justify-center px-6 py-3 rounded-xl bg-[#d4af37] text-black font-bold hover:bg-[#d4af37]/90 transition mb-3"
            data-testid="payment-cancel-retry-btn"
          >
            <RefreshCcw className="w-4 h-4" />
            {isAr ? 'المحاولة مرة أخرى' : 'Try Again'}
          </a>
        )}

        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-2 w-full justify-center px-6 py-3 rounded-xl bg-white/[0.04] border border-white/10 text-white hover:bg-white/[0.08] transition"
          data-testid="payment-cancel-home-btn"
        >
          <Home className="w-4 h-4" />
          {isAr ? 'الصفحة الرئيسية' : 'Home'}
        </button>
      </motion.div>
    </div>
  );
}
